/**
 * Image Helper Utilities
 * Handles image URL construction for both local and external images
 * 
 * Note: Uses relative URLs (no base URL) to work seamlessly in both dev and production:
 * - Dev: Vite proxy forwards /uploads to backend (localhost:5000)
 * - Production: Same server serves both frontend and /uploads
 */

/**
 * Get the full image URL for display
 * Handles both local uploads and external URLs (like Rista API images)
 *
 * @param imageUrl - The image URL from the database (can be relative or absolute)
 * @returns Full image URL ready for use in img src
 *
 * @example
 * getImageUrl("/uploads/items/image.jpg") // "/uploads/items/image.jpg" (proxied in dev, same-origin in prod)
 * getImageUrl("https://external.com/image.jpg") // "https://external.com/image.jpg"
 * getImageUrl(null) // null
 */
export function getImageUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl) {
    return null;
  }

  // If already a full URL (external image), return as-is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // For local uploads, return relative path
  // Vite proxy will forward /uploads to backend in dev
  // In production, same server serves both frontend and uploads
  return imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
}

/**
 * Get placeholder image URL when no image is available
 * @returns Placeholder image URL or null
 */
export function getPlaceholderImage(): string | null {
  // You can add a placeholder image in public folder if needed
  return null;
}

/**
 * Check if image URL is an external URL (not uploaded locally)
 * @param imageUrl - The image URL to check
 * @returns true if external URL, false if local upload
 */
export function isExternalImage(imageUrl: string | null | undefined): boolean {
  if (!imageUrl) return false;
  return imageUrl.startsWith("http://") || imageUrl.startsWith("https://");
}

